import { Checkbox, Col, DatePicker, Form, Input, Radio, Row } from "antd";
import { translate } from "../../../core";
import { useContext } from "react";
import { ContactEntryAddresses } from "./ContactEntryAddresses";
import { ContactEntryPhones } from "./ContactEntryPhones";
import { ContactEntryWebInfos } from "./ContactEntryWebInfos";
import { PageContextValue } from "../../../core/type";
import { ContactEntry, ContactType } from "../type/contact-entry";
import { dateFormat } from "../../../core/until";
import { ContactFormGroup } from "./ContactFormGroup";
import { ContactFormCategory } from "./ContactFormCategory";
import { ContactFormRelations } from "./ContactFormRelations";
import { ContactPageContext } from "./ContactPageContext";
import { Contact } from "../type/contact";
import styles from "./ContactForm.module.scss";
import { InputCard } from "../../inventory/component/InputCard";
import { ContactRelatedUser } from "./ContactRelatedUser";

export const ContactForm = ({
  contactTypeDisabled,
}: {
  contactTypeDisabled?: boolean;
}) => {
  const { save, form, onFormChange, entity } =
    useContext<PageContextValue<Contact, ContactEntry>>(ContactPageContext);

  return (
    <div className="px-3 pb-3">
      <Form
        onFieldsChange={() => {
          onFormChange!();
        }}
        layout="vertical"
        form={form}
        onFinish={save}
        className={styles.formRoot}
      >
        <ContactRelatedUser
          users={entity?.relatedUsers}
        />

        <InputCard contained className="mb-4">
          <Row gutter={[10, 10]}>
            <div className="flex-1 px-4">
              <Form.Item
                label={translate("relation", "group")}
                name={["group", "id"]}
              >
                <ContactFormGroup />
              </Form.Item>
            </div>
            <div className="flex-1 px-4">
              <Form.Item
                label={translate("contact_id")}
                name="contactId"
                rules={[
                  {
                    required: true,
                    message: translate("missing_field_message", "contract_id"),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </div>
            <div className="flex flex-1 mt-3">
              <Form.Item name="contactType">
                <Radio.Group
                  disabled={contactTypeDisabled}
                  className="flex flex-col"
                >
                  {Object.values(ContactType).map((type) => (
                    <Radio
                      key={type}
                      className={["items-center", styles.radio].join(" ")}
                      value={type}
                    >
                      {type}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
              <div>
                <Form.Item
                  className="mb-0"
                  name="allowContact"
                  valuePropName="checked"
                >
                  <Checkbox>{translate("allow_contact")}</Checkbox>
                </Form.Item>
                <Form.Item
                  className="mb-0"
                  name="noEmailMarketing"
                  valuePropName="checked"
                >
                  <Checkbox>{translate("no", "marketing")}</Checkbox>
                </Form.Item>
              </div>
            </div>
          </Row>
        </InputCard>
        <div className={styles.cardGroup}>
          <div className="flex-1">
            <InputCard contained title={translate("company")}>
              <Row gutter={[10, 10]} className="my-4">
                <Col span={12}>
                  <Form.Item
                    label={translate("company", "name")}
                    name="company"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={translate("designer", "tag")}
                    name="designer"
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label={translate("category")}
                    name={["category", "id"]}
                  >
                    <ContactFormCategory />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item label={translate("account")} name="accountNumber">
                    <Input />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    label={translate("markup", "number_abbr")}
                    name="markupNumber"
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item label={translate("percent_off")} name="percentOff">
                    <Input />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item label={translate("promotion")} name="promotion">
                    <Input style={{ width: "100" }} />
                  </Form.Item>
                </Col>
              </Row>
            </InputCard>
          </div>
          <div className="flex-1">
            <InputCard title={translate("contact")} contained>
              <Row gutter={[10, 10]}>
                <Col span={12}>
                  <Form.Item label={translate("first_name")} name="firstName">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={translate("last_name")} name="lastName">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={translate("date_of_birth")}
                    name="dateOfBirth"
                  >
                    <DatePicker format={dateFormat} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={translate("anniversary")}
                    name="anniversary"
                  >
                    <DatePicker format={dateFormat} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
            </InputCard>
            <Form.Item label={translate("notes")} name="note" className="mb-0">
              <Input.TextArea rows={3} />
            </Form.Item>
          </div>
        </div>

        <ContactFormRelations />

        <ContactEntryAddresses />

        <ContactEntryPhones />

        <ContactEntryWebInfos />
      </Form>
    </div>
  );
};
